// /opt/isp_erp/frontend/src/pages/mikrotik/MikroTiksGestion.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Router as RouterIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { listarMikrotiksConSucursal, asignarSucursalMikrotik } from '../../api/mikrotik.api';
import { finanzasAPI } from '../../api/finanzas/finanzas.api';

const MikroTiksGestion = () => {
  const [mikrotiks, setMikrotiks] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMikrotik, setSelectedMikrotik] = useState(null);
  const [selectedSucursal, setSelectedSucursal] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [mikrotiksRes, sucursalesRes] = await Promise.all([
        listarMikrotiksConSucursal(),
        finanzasAPI.getSucursales()
      ]);
      setMikrotiks(mikrotiksRes.data);
      setSucursales(sucursalesRes.data);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mikrotik) => {
    setSelectedMikrotik(mikrotik);
    setSelectedSucursal(mikrotik.sucursal_id || '');
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMikrotik(null);
    setSelectedSucursal('');
  };

  const handleAsignarSucursal = async () => {
    if (!selectedMikrotik) return;
    
    setLoading(true);
    try {
      await asignarSucursalMikrotik(selectedMikrotik.id, selectedSucursal || null);
      await cargarDatos();
      handleCloseDialog();
    } catch (err) {
      console.error('Error:', err);
      setError('Error al asignar sucursal');
    } finally {
      setLoading(false);
    }
  };

  if (loading && mikrotiks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <RouterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Gestión de MikroTiks
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Nombre</TableCell>
              <TableCell>Host</TableCell>
              <TableCell>Sucursal</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mikrotiks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay MikroTiks registrados
                </TableCell>
              </TableRow>
            ) : (
              mikrotiks.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <strong>{m.nombre}</strong>
                  </TableCell>
                  <TableCell>{m.host}</TableCell>
                  <TableCell>
                    {m.sucursal_nombre ? (
                      <Chip
                        icon={<BusinessIcon />}
                        label={m.sucursal_nombre}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="Sin asignar"
                        size="small"
                        color="default"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={m.enabled ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={m.enabled ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(m)}
                      title="Asignar sucursal"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para asignar sucursal */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Asignar Sucursal - {selectedMikrotik?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Sucursal</InputLabel>
              <Select
                value={selectedSucursal}
                onChange={(e) => setSelectedSucursal(e.target.value)}
                label="Sucursal"
              >
                <MenuItem value="">Sin sucursal</MenuItem>
                {sucursales.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.nombre} ({s.codigo})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleAsignarSucursal} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MikroTiksGestion;
